import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BACKEND_URL } from '../config/api';
import { Users, Search, MessageSquare, Phone, TrendingUp, AlertTriangle } from 'lucide-react';

function Stat({ icon: Icon, label, value, hint, testid, tone = 'slate' }) {
  const tones = {
    slate: 'text-slate-900 border-slate-200 bg-white',
    red: 'text-red-700 border-red-200 bg-red-50',
    emerald: 'text-emerald-700 border-emerald-200 bg-emerald-50',
    amber: 'text-amber-700 border-amber-200 bg-amber-50',
  }[tone] || '';
  return (
    <div className={`rounded-xl border p-4 shadow-sm ${tones}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-500 mb-1">
        <Icon className="h-4 w-4" /> {label}
      </div>
      <div className="text-2xl font-bold">{value}</div>
      {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}

function TopList({ title, items, keyField, countField = 'count', emptyLabel = 'None recorded', testid }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm" data-testid={testid}>
      <h4 className="font-semibold text-slate-900 mb-2 text-sm">{title}</h4>
      {items && items.length > 0 ? (
        <ul className="space-y-1">
          {items.map((row, i) => (
            <li key={i} className="flex justify-between text-sm">
              <span className="text-slate-700 truncate pr-2">{row[keyField] || '—'}</span>
              <span className="font-medium text-slate-900">{row[countField]}</span>
            </li>
          ))}
        </ul>
      ) : <div className="text-xs text-slate-500">{emptyLabel}</div>}
    </div>
  );
}

export default function AdminBusinessIntelligence() {
  const [range, setRange] = useState('daily');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true); setError(null);
      try {
        const url = range === 'weekly'
          ? `${BACKEND_URL}/api/admin/activity/summary/weekly?weeks=1`
          : `${BACKEND_URL}/api/admin/activity/summary/daily?days=${range === 'daily' ? 1 : 7}`;
        const res = await axios.get(url, { withCredentials: true });
        setData(res.data);
      } catch (e) {
        setError(e.response?.data?.detail || 'Failed to load summary');
      } finally {
        setLoading(false);
      }
    })();
  }, [range]);

  return (
    <div className="space-y-4" data-testid="admin-bi">
      <div className="flex gap-2" data-testid="admin-bi-range">
        {[
          { k: 'daily', label: 'Today' },
          { k: '7d', label: 'Last 7 days' },
          { k: 'weekly', label: 'This week' },
        ].map((o) => (
          <button
            key={o.k}
            type="button"
            onClick={() => setRange(o.k)}
            className={`px-3 py-1.5 rounded-md text-sm border ${range === o.k ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-slate-700 border-slate-300'}`}
            data-testid={`admin-bi-range-${o.k}`}
          >
            {o.label}
          </button>
        ))}
      </div>

      {loading && <div className="text-sm text-slate-500">Loading…</div>}
      {error && <div className="text-sm text-red-600" data-testid="admin-bi-error">{error}</div>}

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat testid="bi-active-users" icon={Users} label="Active users" value={data.users.active}
                  hint={`${data.users.new} new / ${data.users.returning} returning`} />
            <Stat testid="bi-profiles" icon={Users} label="Profiles completed" value={data.users.completed_profiles}
                  hint={`${data.users.workers_became_searchable} became searchable`} />
            <Stat testid="bi-searches" icon={Search} label="Searches" value={data.searches.total}
                  hint={`${data.searches.zero_result} returned zero`}
                  tone={data.searches.zero_result > 0 ? 'amber' : 'slate'} />
            <Stat testid="bi-conversion" icon={TrendingUp} label="Search → chat"
                  value={`${data.contact.employer_to_worker_conversion_pct}%`}
                  hint={`${data.contact.employer_conversations_started} chats from ${data.contact.employer_searches} searches`}
                  tone="emerald" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Stat testid="bi-phone-reveals" icon={Phone} label="Phone reveals" value={data.contact.phone_reveals} />
            <Stat testid="bi-phone-denied" icon={AlertTriangle} label="Phone reveal 429s"
                  value={data.contact.phone_reveal_denied_429}
                  hint="Rate-limit denials — safety signal"
                  tone={data.contact.phone_reveal_denied_429 > 0 ? 'red' : 'slate'} />
            <Stat testid="bi-chats" icon={MessageSquare} label="Chats started" value={data.contact.employer_conversations_started} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <TopList testid="bi-top-trades" title="Most searched trades"
                     items={data.searches.top_trades} keyField="trade" />
            <TopList testid="bi-top-locations" title="Most searched locations"
                     items={data.searches.top_locations} keyField="outward_postcode" />
            <TopList testid="bi-zero-trades" title="Zero-result searches by trade"
                     items={data.searches.zero_result_top_trades} keyField="trade" />
          </div>

          <div className="text-xs text-slate-400 italic">
            Window: {new Date(data.window.from).toLocaleString('en-GB')} → {new Date(data.window.to).toLocaleString('en-GB')}
          </div>
        </>
      )}
    </div>
  );
}
